import { Stop as StopIcon } from 'components/icons';
import { OFF_ACTION_CLASS } from 'components/buttons/buttonStyle';
import { CallForActionButton, CallForActionButtonTooltip } from 'components/buttons/CallForActionButton';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { useId } from 'react';
import { TimecardListItemData } from 'types/timecards';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { useOpenFinishTimecard } from '../useOpenFinishTimecard';
import { finishBlockerLines } from '../utils/timecardUtils';

/** {@link OFF_ACTION_CLASS} for this button's info tint: its fill and glyph dimmed. */
const OFF_INFO_CLASS = cn(
    OFF_ACTION_CLASS,
    'aria-disabled:bg-grey-200/50 aria-disabled:text-info-darker/50 aria-disabled:hover:bg-grey-200/50 aria-disabled:hover:text-info-darker/50'
);

type Props = {
    timecard: Pick<TimecardListItemData, 'id' | 'finishBlockers'>;
};

/**
 * Finish, off in the new design while the draft has blockers, which its tooltip and its description list. Off, it
 * stays a Tab stop (`focusableWhenDisabled`), so the list opens on focus as on hover.
 */
const FinishTimecardIconButton = ({ timecard }: Props): JSX.Element => {
    const isNewDesign = useIsNewDesign();
    const { openFinish, isFinishing, finishBlockersOf } = useOpenFinishTimecard();
    const reason = isNewDesign ? finishBlockerLines(finishBlockersOf(timecard)).join('\n') : '';
    const reasonId = useId();

    return (
        <>
            <CallForActionButtonTooltip title={reason || t('timecards.finish')}>
                <CallForActionButton
                    variant="contained"
                    shape="rounded"
                    color="info"
                    className={OFF_INFO_CLASS}
                    aria-label={t('timecards.finish')}
                    disabled={!!reason || isFinishing}
                    focusableWhenDisabled={isNewDesign}
                    aria-describedby={reason ? reasonId : undefined}
                    onClick={() => {
                        void openFinish(timecard.id);
                    }}
                >
                    <StopIcon />
                </CallForActionButton>
            </CallForActionButtonTooltip>
            {reason ? (
                <span id={reasonId} hidden>
                    {reason}
                </span>
            ) : null}
        </>
    );
};

export default FinishTimecardIconButton;
